import React, { useState, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Avatar,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import {
  Edit as EditIcon,
  CameraAlt as CameraIcon,
  Save as SaveIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from '../api/axios';

function Profile() {
  const { t, i18n } = useTranslation();
  const { user, refreshUser } = useAuth();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Profile form
  const [profileData, setProfileData] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    language: user?.language || 'ru'
  });

  // Password form
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarTimestamp, setAvatarTimestamp] = useState(Date.now());

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await axios.put('/users/profile/me', profileData);
      setSuccess('Профиль успешно обновлен');
      if (profileData.language !== i18n.language) {
        i18n.changeLanguage(profileData.language);
      }
      if (refreshUser) {
        refreshUser();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка обновления профиля');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('Пароли не совпадают');
      setLoading(false);
      return;
    }

    if (passwordData.new_password.length < 8) {
      setError('Пароль должен быть не менее 8 символов');
      setLoading(false);
      return;
    }

    try {
      await axios.post('/users/profile/change-password', passwordData);
      setSuccess('Пароль успешно изменен');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка изменения пароля');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Допустимые форматы: JPEG, PNG, GIF, WebP');
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Максимальный размер файла: 5MB');
      return;
    }

    setUploadingAvatar(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post('/users/profile/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setSuccess('Аватар успешно обновлен');
      // Update timestamp to force image reload
      setAvatarTimestamp(Date.now());
      if (refreshUser) {
        await refreshUser();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка загрузки аватара');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const getRoleName = () => {
    if (user?.is_superuser) return 'Суперадмин';
    return user?.role?.name || 'Без роли';
  };

  const getRoleColor = () => {
    if (user?.is_superuser) return '#9c27b0';
    return user?.role?.color || '#757575';
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h4" gutterBottom>
        Профиль
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Avatar and Role Info */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
              <Avatar
                src={user?.avatar ? `${user.avatar}?t=${avatarTimestamp}` : undefined}
                sx={{ width: 150, height: 150, fontSize: 48, mx: 'auto' }}
              >
                {user?.full_name?.charAt(0) || user?.email?.charAt(0)}
              </Avatar>
              <IconButton
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': { backgroundColor: 'primary.dark' }
                }}
                onClick={handleAvatarClick}
                disabled={uploadingAvatar}
              >
                {uploadingAvatar ? <CircularProgress size={24} color="inherit" /> : <CameraIcon />}
              </IconButton>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleAvatarChange}
              />
            </Box>

            <Typography variant="h6" sx={{ mt: 2 }}>
              {user?.full_name || 'Без имени'}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {user?.email}
            </Typography>

            <Chip
              label={getRoleName()}
              sx={{
                mt: 2,
                backgroundColor: getRoleColor(),
                color: 'white'
              }}
            />
          </Paper>

          {/* Permissions Card */}
          {user?.permissions && user.permissions.length > 0 && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                  Ваши права
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {user.permissions.slice(0, 10).map((perm) => (
                    <Chip key={perm} label={perm} size="small" variant="outlined" />
                  ))}
                  {user.permissions.length > 10 && (
                    <Chip label={`+${user.permissions.length - 10}`} size="small" />
                  )}
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Profile and Password Forms */}
        <Grid item xs={12} md={8}>
          {/* Profile Form */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Личные данные</Typography>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Box component="form" onSubmit={handleProfileSubmit}>
              <TextField
                margin="normal"
                fullWidth
                label="Полное имя"
                value={profileData.full_name}
                onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
              />

              <TextField
                margin="normal"
                fullWidth
                label="Email"
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
              />

              <FormControl fullWidth margin="normal">
                <InputLabel>Язык интерфейса</InputLabel>
                <Select
                  value={profileData.language}
                  label="Язык интерфейса"
                  onChange={(e) => setProfileData({ ...profileData, language: e.target.value })}
                >
                  <MenuItem value="ru">Русский</MenuItem>
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="uk">Українська</MenuItem>
                </Select>
              </FormControl>

              <Button
                type="submit"
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                sx={{ mt: 2 }}
                disabled={loading}
              >
                Сохранить
              </Button>
            </Box>
          </Paper>

          {/* Password Form */}
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LockIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Изменить пароль</Typography>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Box component="form" onSubmit={handlePasswordSubmit}>
              <TextField
                margin="normal"
                fullWidth
                label="Текущий пароль"
                type="password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                required
              />

              <TextField
                margin="normal"
                fullWidth
                label="Новый пароль"
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                required
                helperText="Минимум 8 символов"
              />

              <TextField
                margin="normal"
                fullWidth
                label="Подтвердите новый пароль"
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                required
              />

              <Button
                type="submit"
                variant="contained"
                color="secondary"
                startIcon={loading ? <CircularProgress size={20} /> : <LockIcon />}
                sx={{ mt: 2 }}
                disabled={loading || !passwordData.current_password || !passwordData.new_password}
              >
                Изменить пароль
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}

export default Profile;
